/**
 * Marketing price catalog: static `/prices` and TV display offerings plus dual-price formatters.
 * Bookable Site prices live in `site_prices` via `pricing.functions` / `admin-pricing.functions`.
 */
export type DualPrice = { label: string; cashCents: number; cardCents: number };

type LessonOffering = DualPrice & {
  kind: "single" | "package";
  durationMinutes: 30 | 60;
  lessonCount: number;
  lessonTypeSlug: "private-golf-30" | "private-golf-60";
};

export function formatDualPrice(cashCents: number, cardCents: number): string {
  const cash = cashCents / 100;
  const card = cardCents / 100;
  const cashStr = cash % 1 === 0 ? `$${cash}` : `$${cash.toFixed(2)}`;
  const cardStr = card % 1 === 0 ? `$${card}` : `$${card.toFixed(2)}`;
  return `${cashStr} / ${cardStr}`;
}

/** TV display board: cash column uses a space after `$` (e.g. `$ 6`). */
export function formatTvCashPrice(cents: number): string {
  const dollars = cents / 100;
  return dollars % 1 === 0 ? `$ ${dollars}` : `$ ${dollars.toFixed(2)}`;
}

/** TV display board: card column always shows cents (e.g. `$ 6.24`). */
export function formatTvCardPrice(cents: number): string {
  return `$ ${(cents / 100).toFixed(2)}`;
}

export const RANGE_BUCKET_TV: Record<string, { size: string; balls: number }> = {
  "Small Bucket": { size: "Small", balls: 25 },
  "Medium Bucket": { size: "Medium", balls: 45 },
  "Large Bucket": { size: "Large", balls: 75 },
  "Extra Large Bucket": { size: "X-Large", balls: 100 },
  "Jumbo Bucket": { size: "Jumbo", balls: 180 },
};

const LESSON_OFFERINGS: LessonOffering[] = [
  {
    label: "A single 30 minute lesson",
    cashCents: 7000,
    cardCents: 7280,
    kind: "single",
    durationMinutes: 30,
    lessonCount: 1,
    lessonTypeSlug: "private-golf-30",
  },
  {
    label: "Series of 5, 30 minute lessons",
    cashCents: 32500,
    cardCents: 33800,
    kind: "package",
    durationMinutes: 30,
    lessonCount: 5,
    lessonTypeSlug: "private-golf-30",
  },
  {
    label: "Series of 10, 30 minute lessons",
    cashCents: 62500,
    cardCents: 65000,
    kind: "package",
    durationMinutes: 30,
    lessonCount: 10,
    lessonTypeSlug: "private-golf-30",
  },
  {
    label: "Series of 15, 30 minute lessons",
    cashCents: 95000,
    cardCents: 98800,
    kind: "package",
    durationMinutes: 30,
    lessonCount: 15,
    lessonTypeSlug: "private-golf-30",
  },
  {
    label: "A single 1 hour lesson",
    cashCents: 13000,
    cardCents: 13520,
    kind: "single",
    durationMinutes: 60,
    lessonCount: 1,
    lessonTypeSlug: "private-golf-60",
  },
  {
    label: "Series of 5, 1 hour lessons",
    cashCents: 60000,
    cardCents: 62400,
    kind: "package",
    durationMinutes: 60,
    lessonCount: 5,
    lessonTypeSlug: "private-golf-60",
  },
  {
    label: "Series of 10, 1 hour lessons",
    cashCents: 115000,
    cardCents: 119600,
    kind: "package",
    durationMinutes: 60,
    lessonCount: 10,
    lessonTypeSlug: "private-golf-60",
  },
];

export const SINGLE_LESSONS = LESSON_OFFERINGS.filter((o) => o.kind === "single");
export const PACKAGE_LESSONS = LESSON_OFFERINGS.filter((o) => o.kind === "package");

export const RANGE_BUCKET_PRICES: DualPrice[] = [
  { label: "Small Bucket", cashCents: 600, cardCents: 624 },
  { label: "Medium Bucket", cashCents: 800, cardCents: 832 },
  { label: "Large Bucket", cashCents: 1200, cardCents: 1248 },
  { label: "Extra Large Bucket", cashCents: 1400, cardCents: 1456 },
  { label: "Jumbo Bucket", cashCents: 2200, cardCents: 2288 },
];

export const GIFT_CARD_STARTING = RANGE_BUCKET_PRICES[0];

export const BOOKABLE_LESSON_SLUGS = new Set(["private-golf-30", "private-golf-60"]);
